import React from "react";
import { MapPin, Trash2 } from "lucide-react";
import { cn } from "lib/utils";
import { useTargeting, type TargetingArea } from "./TargetingContext";
import { useAreaCardFocus } from "./useAreaCardFocus";

interface AreaCardProps {
  area: TargetingArea;
  onRemove?: (area: TargetingArea) => void;
  /** Overrides the card label — D2D shows "Area N" instead of the area value. */
  displayName?: string;
  /**
   * Inline detail rendered next to the name (D2D = "📄 N flyers"). When set, the
   * leading pin and the right-aligned "Units:" readout are suppressed — the meta
   * owns the unit presentation (matches the D2D design).
   */
  meta?: React.ReactNode;
}

// One selected distribution-area card in the list below the map. Used as-is for
// D2D (single-row: name + flyers + remove); H2H uses the richer H2hAreaCard. When
// the map's "View" confirmation targets this card it scrolls into view and
// flashes a focus ring (AC23).
export const AreaCard = ({ area, onRemove, displayName, meta }: AreaCardProps) => {
  const { focusOnMap, hoveredAreaId, setHoveredArea } = useTargeting();
  const { ref, focused } = useAreaCardFocus(area.id);
  const hovered = hoveredAreaId === area.id;

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={() => focusOnMap(area.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          focusOnMap(area.id);
        }
      }}
      // Hovering the card mirrors onto the map (AC24): the polygon/pin highlights.
      onMouseEnter={() => setHoveredArea(area.id)}
      onMouseLeave={() => setHoveredArea(null)}
      className={cn(
        // transition-all (not just colors) so the focus ring eases out over ~700ms
        // when the flash ends, instead of snapping off.
        // scroll-my keeps a gap from the viewport edge when scrolled into view.
        "flex scroll-my-6 cursor-pointer items-center justify-between gap-3 rounded-md border border-solid bg-background p-4 transition-all duration-700 hover:border-slate-400 dark:hover:border-slate-500",
        focused
          ? "border-primary ring-2 ring-primary/40"
          : hovered
          ? "border-slate-400 dark:border-slate-500"
          : "border-border"
      )}
      data-cypress-id={`targeting-area-card-${area.id}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        {!meta && <MapPin className="size-4 shrink-0 text-muted-foreground" />}
        <span className="truncate text-sm font-medium text-foreground">{displayName ?? area.value}</span>
        {meta}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {!meta && typeof area.units === "number" && (
          <span className="text-sm text-muted-foreground">
            Units: <span className="font-medium text-foreground">{area.units}</span>
          </span>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(area);
            }}
            aria-label="Remove area"
            data-cypress-id={`targeting-area-remove-${area.id}`}
            className="text-foreground transition-colors hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
};
